import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import UserHeader from "@/components/user/UserHeader";
import Footer from "@/components/layout/Footer";
import { Home } from "lucide-react";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";

const Emission: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [kgCO2eValue, setKgCO2eValue] = useState("0.50");
  const [projectType, setProjectType] = useState("");
  const [activityType, setActivityType] = useState<string[]>([]);
  const [searchDocument, setSearchDocument] = useState("");
  const [otherActivity, setOtherActivity] = useState("");
  const [projetosDialogOpen, setProjetosDialogOpen] = useState(false);

  const isActivePath = (path: string): boolean => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    if (path === 'projects') {
      return ['/my-projects', '/project/status', '/project/region', '/all-projects'].some(
        p => location.pathname.startsWith(p)
      );
    }
    return location.pathname === path;
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleProjetosClick = () => {
    setProjetosDialogOpen(true);
  };

  const handleActivityChange = (value: string) => {
    if (activityType.includes(value)) {
      setActivityType(activityType.filter(item => item !== value));
    } else {
      setActivityType([...activityType, value]);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <UserHeader />
      {/* Menu horizontal superior */}
      <div className="bg-white border-b border-gray-200 px-6">
        <div className="flex space-x-6 overflow-x-auto py-3">
          <button 
            className={`${isActivePath('/planos') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/planos')}
          >
            Planos
          </button>
          <button 
            className={`${isActivePath('/dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`${isActivePath('projects') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={handleProjetosClick}
          >
            Projetos
          </button>
          <button 
            className={`${isActivePath('/emission') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/emission')}
          >
            Emissão
          </button>
          <button 
            className={`${isActivePath('/traceability') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/traceability')}
          >
            Rastreabilidade
          </button>
          <button 
            className={`${isActivePath('/certificates') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/certificates')}
          >
            Certificados
          </button>
          <button
            className={`${isActivePath('/pregao-dashboard') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/pregao-dashboard')}
          >
            Bolsa de Crédito de Carbono Voluntário
          </button>
          <button 
            className={`${isActivePath('/compliance') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/compliance')}
          >
            Conformidade
          </button>
          <button 
            className={`${isActivePath('/retirement') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/retirement')}
          >
            Aposentadoria
          </button>
          <button 
            className={`${isActivePath('/educational') ? 'text-[#0e7cb4] font-medium border-b-2 border-[#0e7cb4]' : 'text-gray-500 hover:text-[#0e7cb4]'} px-2 py-1 min-w-max`}
            onClick={() => handleNavigation('/educational')}
          >
            Espaço Educativo
          </button>
        </div>
      </div>
      {/* Pop-up de Acesso para Projetos */}
      <Dialog open={projetosDialogOpen} onOpenChange={setProjetosDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center text-xl">Acesso a Projetos</DialogTitle>
            <DialogDescription className="text-center">
              Selecione o tipo de visualização de projetos que deseja acessar.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/my-projects');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Meus Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Visualize todos os seus projetos cadastrados</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/status');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Status dos Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acompanhe o status de todos os projetos</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              setProjetosDialogOpen(false);
              handleNavigation('/project/region');
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Projetos por Região</h3>
                <p className="text-sm text-gray-500 text-center">Explore projetos por localização geográfica</p>
              </CardContent>
            </Card>

            <Card className="border border-gray-200 hover:border-[#0e7cb4] hover:shadow-md cursor-pointer transition-all" onClick={() => {
              window.open('https://banclima.com/projetos', '_blank');
              setProjetosDialogOpen(false);
            }}>
              <CardContent className="p-6 flex flex-col items-center justify-center">
                <div className="bg-[#0e7cb4]/10 p-3 rounded-full mb-3">
                  <Home className="h-6 w-6 text-[#0e7cb4]" />
                </div>
                <h3 className="text-lg font-medium mb-1">Portal de Projetos</h3>
                <p className="text-sm text-gray-500 text-center">Acesse o portal público de projetos do Banclima</p>
              </CardContent>
            </Card>
          </div>
          <DialogFooter className="sm:justify-center">
            <Button 
              variant="outline" 
              onClick={() => setProjetosDialogOpen(false)}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="container mx-auto px-4 py-6 flex-grow">
        <div className="bg-white p-10 rounded-lg shadow-md mb-6 min-h-[500px]">
          <h2 className="text-2xl font-bold text-[#0e7cb4] mb-4">Total de emissões evitadas - 1861,44</h2>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="mr-1">Projeto</span>
            <select className="border rounded px-2 py-1">
              <option>Cacucia Colônia Z10</option>
              {/* Adicione mais opções conforme necessário */}
            </select>
            <span className="mr-1">Status</span>
            <select className="border rounded px-2 py-1">
              <option>Todos</option>
              <option>Ativo</option>
              <option>Inativo</option>
            </select>
            <input type="date" className="border rounded px-2 py-1" defaultValue="2025-06-01" />
            <span className="mx-1">a</span>
            <input type="date" className="border rounded px-2 py-1" defaultValue="2025-07-03" />
            <button className="border rounded px-3 py-1">Limpar</button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs sm:text-sm">
              <thead>
                <tr className="text-left">
                  <th className="pr-2">ID</th>
                  <th className="pr-2">Projeto</th>
                  <th className="pr-2">Cliente</th>
                  <th className="pr-2">CPF</th>
                  <th className="pr-2">Mês</th>
                  <th className="pr-2">Evitadas (KgCO2e)</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { id: 18, projeto: 'Cacucia Colônia Z10', cliente: 'Ingrid Quintanilha de Araújo', cpf: '060.691.254-10', mes: 'Junho', evitadas: '0.000' },
                  { id: 68, projeto: 'Cacucia Colônia Z10', cliente: 'Juliene de Albuquerque Travasso', cpf: '120.195.647-18', mes: 'Junho', evitadas: '0.000' },
                  { id: 19, projeto: 'Cacucia Colônia Z10', cliente: 'Janelice de Albuquerque Beltrão', cpf: '946.939.197-72', mes: 'Junho', evitadas: '0.000' },
                  { id: 25, projeto: 'Cacucia Colônia Z10', cliente: 'Luiz Cláudio barbosa', cpf: '022.030.897-76', mes: 'Junho', evitadas: '0.000' },
                  { id: 69, projeto: 'Cacucia Colônia Z10', cliente: 'Pablo da Silva Felipe', cpf: '115.141.057-80', mes: 'Junho', evitadas: '46.449' },
                  { id: 68, projeto: 'Cacucia Colônia Z10', cliente: 'Marcos Antônio Campelo da Silva', cpf: '742.311.117-15', mes: 'Junho', evitadas: '85.669' },
                  { id: 70, projeto: 'Cacucia Colônia Z10', cliente: 'Tereza Machado de Faria de Oliveira', cpf: '760.912.877-53', mes: 'Junho', evitadas: '0.000' },
                  { id: 27, projeto: 'Cacucia Colônia Z10', cliente: 'Rafaella da Silva Emidio de Souza', cpf: '112.978.507-69', mes: 'Junho', evitadas: '17.841' },
                  { id: 21, projeto: 'Cacucia Colônia Z10', cliente: 'Fernanda Diniz Travasso', cpf: '117.882.117-07', mes: 'Junho', evitadas: '36.759' },
                  { id: 69, projeto: 'Cacucia Colônia Z10', cliente: 'Pablo da Silva Felipe', cpf: '115.141.057-80', mes: 'Junho', evitadas: '0.000' },
                ].map((item) => (
                  <tr key={item.id + item.cpf} className="h-14 align-middle">
                    <td className="pr-2 whitespace-nowrap">{item.id}</td>
                    <td className="pr-2 whitespace-nowrap">{item.projeto}</td>
                    <td className="pr-2 whitespace-nowrap">{item.cliente}</td>
                    <td className="pr-2 whitespace-nowrap">{item.cpf}</td>
                    <td className="pr-2 whitespace-nowrap">{item.mes}</td>
                    <td className="pr-2 whitespace-nowrap">{item.evitadas}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <Tabs defaultValue="about" className="w-full">
          <TabsList className="w-full bg-white border-b border-gray-200 p-0">
            <TabsTrigger value="about" className="flex-1 py-3">O Que é Crédito de Carbono</TabsTrigger>
            <TabsTrigger value="process" className="flex-1 py-3">Como Funciona a Emissão</TabsTrigger>
            <TabsTrigger value="steps" className="flex-1 py-3">Etapas do Processo</TabsTrigger>
            <TabsTrigger value="benefits" className="flex-1 py-3">Benefícios</TabsTrigger>
          </TabsList>
          <div className="bg-white p-6 rounded-lg shadow-md">
            <TabsContent value="about" className="mt-0">
              <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">O que é Crédito de Carbono?</h2>
              <p className="text-gray-700 mb-4">
                Créditos de carbono são certificados que representam a redução ou remoção de gases de efeito 
                estufa (GEE) da atmosfera. Cada crédito equivale a uma tonelada de dióxido de carbono (CO₂) 
                ou gases equivalentes que deixaram de ser emitidos ou foram removidos da atmosfera.
              </p>
              <p className="text-gray-700 mb-4">
                Eles funcionam como uma "moeda" no mercado de carbono, permitindo que empresas e indivíduos 
                compensem suas emissões inevitáveis ao investir em projetos que reduzem ou removem GEE em 
                outros lugares.
              </p>
              <p className="text-gray-700">
                Esses créditos são essenciais para alcançar as metas climáticas globais, fornecendo 
                incentivos financeiros para iniciativas ambientais e permitindo que organizações atinjam 
                suas metas de neutralidade de carbono.
              </p>
            </TabsContent>
            
            <TabsContent value="process" className="mt-0">
              <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Como Funciona a Emissão de Créditos de Carbono</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800">Definição de Projeto</h3>
                  <p className="text-gray-700">
                    Um projeto que gera créditos de carbono deve demonstrar que reduz ou remove GEE 
                    de forma mensurável, comparado ao que ocorreria em sua ausência (chamado de "linha de base"). 
                    Exemplos incluem projetos de energia renovável, reflorestamento, eficiência energética 
                    e gestão de resíduos.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-800">Metodologias</h3>
                  <p className="text-gray-700">
                    São utilizadas metodologias certificadas por padrões internacionais como VCS (Verified Carbon 
                    Standard), Gold Standard, MDL (Mecanismo de Desenvolvimento Limpo) ou padrões nacionais. 
                    Estas metodologias definem como calcular, monitorar e verificar as reduções ou remoções de GEE.
                  </p>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-800">Certificação</h3>
                  <p className="text-gray-700">
                    Auditores independentes verificam se o projeto está implementando corretamente a metodologia 
                    e se as reduções ou remoções de GEE são reais, mensuráveis e adicionais. Esta verificação 
                    garante a credibilidade dos créditos gerados.
                  </p>
                </div>
              </div>
            </TabsContent>
            
            <TabsContent value="steps" className="mt-0">
              <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Etapas do Processo de Emissão de Créditos de Carbono</h2>
              <ol className="list-decimal list-inside space-y-3 text-gray-700">
                <li className="font-medium">
                  Cadastro do Projeto
                  <p className="font-normal ml-6 mt-1">
                    O desenvolvedor do projeto registra sua iniciativa na plataforma Banclima, 
                    fornecendo informações detalhadas sobre localização, escopo, metodologia 
                    utilizada e estimativas de redução/remoção de GEE.
                  </p>
                </li>
                
                <li className="font-medium">
                  Monitoramento e Verificação
                  <p className="font-normal ml-6 mt-1">
                    O projeto implementa um plano de monitoramento contínuo para coletar dados 
                    sobre suas atividades e impacto em termos de GEE. Periodicamente, um auditor 
                    externo credenciado verifica estes dados e confirma as reduções/remoções.
                  </p>
                </li>
                
                <li className="font-medium">
                  Geração de Créditos
                  <p className="font-normal ml-6 mt-1">
                    Após a verificação, são gerados créditos de carbono proporcionais às 
                    toneladas de CO₂ equivalente reduzidas ou removidas. Cada crédito recebe 
                    um número de série único para garantir sua rastreabilidade.
                  </p>
                </li>
                
                <li className="font-medium">
                  Certificação e Registro
                  <p className="font-normal ml-6 mt-1">
                    Os créditos são oficialmente certificados e registrados no sistema Banclima, 
                    onde recebem documentação formal e são incluídos no registro público para 
                    evitar dupla contagem e garantir transparência.
                  </p>
                </li>
              </ol>
            </TabsContent>
            
            <TabsContent value="benefits" className="mt-0">
              <h2 className="text-xl font-semibold mb-3 text-[#0e7cb4]">Benefícios da Emissão de Créditos de Carbono</h2>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-800">Para Projetos</h3>
                  <ul className="list-disc list-inside text-gray-700 ml-2">
                    <li>Fonte adicional de receita que pode tornar viáveis projetos ambientalmente positivos</li>
                    <li>Reconhecimento formal das contribuições para mitigação das mudanças climáticas</li>
                    <li>Acesso a financiamento internacional e apoio técnico</li>
                    <li>Desenvolvimento de capacidades locais e transferência de tecnologia</li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-lg font-medium text-gray-800">Para Empresas</h3>
                  <ul className="list-disc list-inside text-gray-700 ml-2">
                    <li>Cumprimento de metas de neutralidade de carbono e compromissos ESG</li>
                    <li>Melhoria da reputação e imagem corporativa perante consumidores e investidores</li>
                    <li>Antecipação a regulamentações futuras relacionadas a emissões</li>
                    <li>Potenciais vantagens fiscais e competitivas no mercado</li>
                  </ul>
                </div>
                
                <div className="p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <h3 className="text-lg font-medium text-blue-800 mb-2">Custos e Taxas Associadas</h3>
                  <p className="text-blue-700 mb-2">
                    O processo de emissão de créditos envolve alguns custos que variam conforme o tipo e escala do projeto:
                  </p>
                  <ul className="list-disc list-inside text-blue-700 ml-2">
                    <li>Taxas de registro no sistema Banclima</li>
                    <li>Custos de desenvolvimento de metodologia (quando aplicável)</li>
                    <li>Despesas com verificação e auditoria por terceiros</li>
                    <li>Taxas de emissão por crédito gerado</li>
                  </ul>
                </div>
              </div>
            </TabsContent>
          </div>
        </Tabs>

        <div className="bg-white p-6 rounded-lg shadow-md mt-6">
          <h2 className="text-xl font-semibold mb-4 text-[#0e7cb4]">Exemplos de Projetos de Sucesso</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle>Moeda Social Carbono</CardTitle>
                <CardDescription>Rio de Janeiro, RJ</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  Projeto que conecta iniciativas de sustentabilidade com a economia local, 
                  gerando tokens que são aceitos como meio de pagamento por comércios locais 
                  e incentivando práticas sustentáveis na comunidade.
                </p>
                <p className="text-green-600 mt-2 font-medium">
                  Créditos gerados: 100 tCO₂e
                </p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Plante para Neutralizar</CardTitle>
                <CardDescription>Rio de Janeiro, RJ</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  Iniciativa de reflorestamento que combina plantio de espécies nativas com 
                  educação ambiental e engajamento comunitário, garantindo a absorção de carbono 
                  e preservação da biodiversidade local.
                </p>
                <p className="text-green-600 mt-2 font-medium">
                  Créditos gerados: 500 tCO₂e
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md mt-6">
          <h2 className="text-xl font-semibold mb-4 text-[#0e7cb4]">Perguntas Frequentes (FAQ)</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-medium text-gray-800">Qual a diferença entre créditos de remoção e redução?</h3>
              <p className="text-gray-700">
                Créditos de remoção são gerados por projetos que efetivamente removem CO₂ da atmosfera 
                (como reflorestamento), enquanto créditos de redução vêm de projetos que evitam 
                que novas emissões ocorram (como energia renovável substituindo combustíveis fósseis).
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-800">Por quanto tempo um crédito de carbono é válido?</h3>
              <p className="text-gray-700">
                Na plataforma Banclima, os créditos não expiram após serem emitidos. No entanto, 
                uma vez utilizados para compensação (aposentados), não podem ser vendidos ou transferidos novamente.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-gray-800">Como garantir que um crédito represente uma redução real de emissões?</h3>
              <p className="text-gray-700">
                Através de metodologias rigorosas, verificação por auditores independentes, 
                e rastreabilidade completa no sistema Banclima, que garante que cada crédito 
                seja único, representando uma tonelada de CO₂e efetivamente reduzida ou removida.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Emission; 